import { SegmentStrategy } from "../../rules/intoSegments"
import { RuleSet } from "../../rules/ruleSet"

const ancientGreekRules: RuleSet = {
  preprocess: ["lowerCase", "cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  rules: [
    { comment: 'first do conversions dependent on the next symbol' },
    { from: "γ", to: "ŋ", next: { or: ["/γ/", "/κ/", "/ξ/", "/χ/"] } },
    { from: "σ", to: "z", next: { or: ["/β/", "/γ/", "/μ/"] } },
    { comment: '', isBlankLine: true },
    { comment: 'simple conversions' },
    { from: "α", to: "a" },
    { from: "β", to: "b" },
    { from: "γ", to: "ɡ" },
    { from: "δ", to: "d̪" },
    { from: "ε", to: "e" },
    { from: "ζ", to: "zd" },
    { from: "η", to: "ɛː" },
    { from: "θ", to: "θ" },
    { from: "ι", to: "i" },
    { from: "κ", to: "k" },
    { from: "λ", to: "l" },
    { from: "μ", to: "m" },
    { from: "ν", to: "n" },
    { from: "ξ", to: "ks" },
    { from: "ο", to: "o" },
    { from: "π", to: "p" },
    { from: "ρ", to: "r" },
    { from: "σ", to: "s" },
    { from: "ς", to: "s" },
    { from: "τ", to: "t" },
    { from: "υ", to: "y" },
    { from: "φ", to: "pʰ" },
    { from: "χ", to: "kʰ" },
    { from: "ψ", to: "ps" },
    { from: "ω", to: "ɔː" }
  ],
  stressRule: { "default": 1 }
};

const esperantoRules: RuleSet = {
  preprocess: ["lowerCase", "cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  rules: [
    { from: "c", to: "t͡s" },
    { from: "ĉ", to: "t͡ʃ" },
    { from: "g", to: "ɡ" },
    { from: "ĝ", to: "d͡ʒ" },
    { from: "ĥ", to: "x" },
    { from: "ĵ", to: "ʒ" },
    { from: "n", to: "ŋ", next: { or: ["/ɡ/", "/k/", "/x/"] } },
    { from: "ŝ", to: "ʃ" },
    { from: "ŭ", to: "u̯" }
  ],
  stressRule: { "default": 1 }
};

const futhorcRules: RuleSet = {
  preprocess: ["cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  rules: [
    { comment: 'convert vowels first so later rules can check for vowels' },
    { from: "ᚪ", to: "ɑ" },
    { from: "ᚫ", to: "æ" },
    { from: "ᛖ", to: "e" },
    { from: "ᛠ", to: "æɑ" },
    { from: "ᛁ", to: "i" },
    { from: "ᛇ", to: "ï" },
    { from: "ᚩ", to: "o" },
    { from: "ᛟ", to: "ø" },
    { from: "ᚢ", to: "u" },
    { from: "ᚣ", to: "y" },
    { comment: '', isBlankLine: true },
    { comment: 'convert consonants' },
    { from: "ᛒ", to: "b" },
    { from: "ᚳ", to: "k" },
    { from: "ᛞ", to: "d" },
    { from: "ᚠ", to: "v", previous: ["vowel"], next: ["vowel"] },
    { from: "ᚠ", to: "f" },
    { from: "ᚷ", to: "ɣ", previous: ["vowel"], next: ["vowel"] },
    { from: "ᚷ", to: "ɡ" },
    { from: "ᚻ", to: "h" },
    { from: "ᚺ", to: "h" },
    { from: "ᛡ", to: "j" },
    { from: "ᛄ", to: "j" },
    { from: "ᛚ", to: "l" },
    { from: "ᛗ", to: "m" },
    { from: "ᚾ", to: "n" },
    { from: "ᛝ", to: "ŋɡ" },
    { from: "ᛈ", to: "p" },
    { from: "ᚱ", to: "r" },
    { from: "ᛋ", to: "z", previous: ["vowel"], next: ["vowel"] },
    { from: "ᛋ", to: "s" },
    { from: "ᚴ", to: "z", previous: ["vowel"], next: ["vowel"] },
    { from: "ᚴ", to: "s" },
    { from: "ᛏ", to: "t" },
    { from: "ᚦ", to: "ð", previous: ["vowel"], next: ["vowel"] },
    { from: "ᚦ", to: "θ" },
    { from: "ᚹ", to: "w" },
    { from: "ᛉ", to: "ks" }],
  stressRule: {
    "default": 1,
    toCheck: [{
      which: 2,
      pattern: {
        seq: ['#word', { or: ['/for/', { seq: ['/je/', ':consonant'] },] },]
      }
    }]
  }
};

const hiraganaRules: RuleSet = {
  preprocess: ["cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  rules: [
    { comment: 'convert symbol to a syllable' },
    { from: "あ", to: "a" }, { from: "い", to: "i" }, { from: "う", to: "ɯ" }, { from: "え", to: "e" }, { from: "お", to: "o" }, { from: "ん", to: "n̩" },
    { from: "きゃ", to: "kʲa" }, { from: "きゅ", to: "kʲɯ" }, { from: "きょ", to: "kʲo" }, { from: "か", to: "ka" }, { from: "き", to: "ki" }, { from: "く", to: "kɯ" }, { from: "け", to: "ke" }, { from: "こ", to: "ko" },
    { from: "しゃ", to: "ɕa" }, { from: "しゅ", to: "ɕɯ" }, { from: "しょ", to: "ɕo" }, { from: "さ", to: "sa" }, { from: "し", to: "ɕi" }, { from: "す", to: "sɯ" }, { from: "せ", to: "se" }, { from: "そ", to: "so" },
    { from: "ちゃ", to: "t͡ɕa" }, { from: "ちゅ", to: "t͡ɕɯ" }, { from: "ちょ", to: "t͡ɕo" }, { from: "た", to: "ta" }, { from: "ち", to: "t͡ɕi" }, { from: "つ", to: "t͡sɯ" }, { from: "て", to: "te" }, { from: "と", to: "to" },
    { from: "にゃ", to: "ɲa" }, { from: "にゅ", to: "ɲɯ" }, { from: "にょ", to: "ɲo" }, { from: "な", to: "na" }, { from: "に", to: "ɲi" }, { from: "ぬ", to: "nɯ" }, { from: "ね", to: "ne" }, { from: "の", to: "no" },
    { from: "ひゃ", to: "ça" }, { from: "ひゅ", to: "çɯ" }, { from: "ひょ", to: "ço" }, { from: "は", to: "ha" }, { from: "ひ", to: "çi" }, { from: "ふ", to: "ɸɯ" }, { from: "へ", to: "he" }, { from: "ほ", to: "ho" },
    { from: "みゃ", to: "mʲa" }, { from: "みゅ", to: "mʲɯ" }, { from: "みょ", to: "mʲo" }, { from: "ま", to: "ma" }, { from: "み", to: "mi" }, { from: "む", to: "mɯ" }, { from: "め", to: "me" }, { from: "も", to: "mo" },
    { from: "や", to: "ja" }, { from: "ゆ", to: "jɯ" }, { from: "𛀁", to: "e" }, { from: "よ", to: "jo" },
    { from: "りゃ", to: "ɾʲa" }, { from: "りゅ", to: "ɾʲɯ" }, { from: "りょ", to: "ɾʲo" }, { from: "ら", to: "ɾa" }, { from: "り", to: "ɾi" }, { from: "る", to: "ɾɯ" }, { from: "れ", to: "ɾe" }, { from: "ろ", to: "ɾo" },
    { from: "わ", to: "wa" }, { from: "ゐ", to: "i" }, { from: "ゑ", to: "e" }, { from: "を", to: "o" },
    { from: "ぎゃ", to: "ɡʲa" }, { from: "ぎゅ", to: "ɡʲɯ" }, { from: "ぎょ", to: "ɡʲo" }, { from: "が", to: "ɡa" }, { from: "ぎ", to: "ɡi" }, { from: "ぐ", to: "ɡɯ" }, { from: "げ", to: "ɡe" }, { from: "ご", to: "ɡo" },
    { from: "じゃ", to: "d͡ʑa" }, { from: "じゅ", to: "d͡ʑɯ" }, { from: "じょ", to: "d͡ʑo" }, { from: "ざ", to: "d͡za" }, { from: "じ", to: "d͡ʑi" }, { from: "ず", to: "d͡zɯ" }, { from: "ぜ", to: "d͡ze" }, { from: "ぞ", to: "d͡zo" },
    { from: "ぢゃ", to: "d͡ʑa" }, { from: "ぢゅ", to: "d͡ʑɯ" }, { from: "ぢょ", to: "d͡ʑo" }, { from: "だ", to: "da" }, { from: "ぢ", to: "d͡ʑi" }, { from: "づ", to: "d͡zɯ" }, { from: "で", to: "de" }, { from: "ど", to: "do" },
    { from: "びゃ", to: "bʲa" }, { from: "びゅ", to: "bʲɯ" }, { from: "びょ", to: "bʲo" }, { from: "ば", to: "ba" }, { from: "び", to: "bi" }, { from: "ぶ", to: "bɯ" }, { from: "べ", to: "be" }, { from: "ぼ", to: "bo" },
    { from: "ぴゃ", to: "pʲa" }, { from: "ぴゅ", to: "pʲɯ" }, { from: "ぴょ", to: "pʲo" }, { from: "ぱ", to: "pa" }, { from: "ぴ", to: "pi" }, { from: "ぷ", to: "pɯ" }, { from: "ぺ", to: "pe" }, { from: "ぽ", to: "po" },
    { comment: '', isBlankLine: true },
    { comment: 'special symbols' },
    { from: "っ", to: "ʔ", next: ['#word'] },
    { from: "っ", to: "t", next: 't͡ɕ' }, // e.g., こっち > [kott͡ɕi]
    { from: "っ", to: "ː", post: 'swapWithNext' }, // makes the *next* phoneme longer, e.g., ざっし > [d͡zaɕːi], あっさり > [asːari]
    { from: "ー", to: "ː" }, // e.g., らーめん > [ɾaːmen]
    { comment: '', isBlankLine: true },
    { comment: 'punctuation' },
    { from: "。", to: "‖" }, { from: "、", to: "|" },
    { from: "（", to: "|" }, { from: "）", to: "|" },
    { from: "「", to: "|" }, { from: "」", to: "|" },
  ],
  rules2: [
    { comment: 'adjustments to make after everything is in IPA' },
    { from: "ei", to: "eː" },
    { from: "oɯ", to: "oː" },
    { from: "n", to: "ŋ", next: { or: ["/k/", "/ɡ/"] } },
  ],
};

const pinyinRules: RuleSet = {
  preprocess: ["lowerCase", "cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  inputSegments2: SegmentStrategy.PreferIpaPlus,
  rules: [
    { comment: "https://en.wikipedia.org/wiki/Pinyin" },
    { comment: "https://www.fluentu.com/blog/chinese/learn-pinyin/" },
    { comment: "", "isBlankLine": true },
    { comment: "convert single-symbols to vowel+tone" },
    { from: "ā", to: "a¹" },
    { from: "ē", to: "e¹" },
    { from: "ī", to: "i¹" },
    { from: "ō", to: "o¹" },
    { from: "ū", to: "u¹" },
    { from: "ǖ", to: "ü¹" },
    { comment: "", "isBlankLine": true },
    { from: "á", to: "a²" },
    { from: "é", to: "e²" },
    { from: "ế", to: "ɛ²" },
    { from: "í", to: "i²" },
    { from: "ó", to: "o²" },
    { from: "ú", to: "u²" },
    { from: "ǘ", to: "ü²" },
    { comment: "", "isBlankLine": true },
    { from: "ǎ", to: "a³" },
    { from: "ă", to: "a³" },
    { from: "ě", to: "e³" },
    { from: "ǐ", to: "i³" },
    { from: "ĭ", to: "i³" },
    { from: "ǒ", to: "o³" },
    { from: "ŏ", to: "o³" },
    { from: "ǔ", to: "u³" },
    { from: "ǚ", to: "ü³" },
    { comment: "", "isBlankLine": true },
    { from: "à", to: "a⁴" },
    { from: "è", to: "e⁴" },
    { from: "ề", to: "ɛ⁴" },
    { from: "ì", to: "i⁴" },
    { from: "ò", to: "o⁴" },
    { from: "ù", to: "u⁴" },
    { from: "ǜ", to: "ü⁴" },
    { comment: "", "isBlankLine": true },
    { comment: "remaining tone diacritics" },
    { from: "̄", to: "¹" },
    { from: "ˉ", to: "¹" },
    { from: "́", to: "²" },
    { from: "ˊ", to: "²" },
    { from: "̌", to: "³" },
    { from: "ˇ", to: "³" },
    { from: "̀", to: "⁴" },
    { from: "ˋ", to: "⁴" },
    { from: "·", to: "" },
    { comment: "", "isBlankLine": true },
    { comment: "convert to IPA" },
    { from: "ê", to: "ɛ" },
    { from: "ü", to: "ü" },
    { from: "ĉ", to: "ch" },
    { from: "ŝ", to: "sh" },
    { from: "ẑ", to: "zh" },
    { from: "ng", to: "ŋ" },
    { comment: "", "isBlankLine": true },
    { comment: "punctuation" },
    { from: "。", to: "‖" },
    { from: "，", to: "|" },
    { from: "、", to: "|" },
    { from: "—", to: "|" },
    { from: "…", to: "|" },
    { from: "'", to: "." },
    { from: "-", to: "" }
  ],
  rules2: [
    [
      { comment: "yu- -ü-" },
      { from: "yua", to: "ɥɛ", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "üa", to: "ɥɛ", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "yo", to: "jʊ", next: { or: ["/ŋ/", "/¹ŋ/", "/²ŋ/", "/³ŋ/", "/⁴ŋ/"] } },
      { from: "io", to: "jʊ", next: { or: ["/ŋ/", "/¹ŋ/", "/²ŋ/", "/³ŋ/", "/⁴ŋ/"] } },
      { from: "yu", to: "yː", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "ü", to: "y", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "yue", to: "ɥe" },
      { from: "ü", to: "ɥ", next: { or: ["/a/", "/e/"] } },
      { from: "yu", to: "yː" },
      { comment: "", isBlankLine: true },
      { comment: "y- -i-" },
      { from: "ye", to: "je" },
      { from: "ie", to: "je" },
      { from: "you", to: "jou̯" },
      { from: "iu", to: "jou̯" },
      { from: "ya", to: "jɛ", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "ia", to: "jɛ", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "yi", to: "i", next: { or: ["/ŋ/", "/¹ŋ/", "/²ŋ/", "/³ŋ/", "/⁴ŋ/"] } },
      { from: "e", to: "ə", next: { or: ["/ŋ/", "/¹ŋ/", "/²ŋ/", "/³ŋ/", "/⁴ŋ/"] } },
      { comment: "", isBlankLine: true },
      { comment: "w- -u-" },
      { from: "ui", to: "wei̯" },
      { from: "u", to: "w", next: { or: ["/ei/", "/ai/", "/an/", "/aŋ/", "/o/", "/a/", "/ən/", "/e/"] } },
      { comment: "e.g., gōng-guān is wrong using..." },
      { comment: "o > wə / _{ŋ|¹ŋ|²ŋ|³ŋ|⁴ŋ}" },
      { from: "i", to: "ɨ", previous: { or: ["/z/", "/c/", "/s/", "/zh/", "/ch/", "/sh/", "/r/"] }, next: ["#word"] },
      { from: "i¹", to: "ɨ¹", previous: { or: ["/z/", "/c/", "/s/", "/zh/", "/ch/", "/sh/", "/r/"] }, next: ["#word"] },
      { from: "i²", to: "ɨ²", previous: { or: ["/z/", "/c/", "/s/", "/zh/", "/ch/", "/sh/", "/r/"] }, next: ["#word"] },
      { from: "i³", to: "ɨ³", previous: { or: ["/z/", "/c/", "/s/", "/zh/", "/ch/", "/sh/", "/r/"] }, next: ["#word"] },
      { from: "i⁴", to: "ɨ⁴", previous: { or: ["/z/", "/c/", "/s/", "/zh/", "/ch/", "/sh/", "/r/"] }, next: ["#word"] },
      { comment: "", isBlankLine: true },
      { comment: "diphthongs" },
      { from: "a¹i", to: "ai̯¹" },
      { from: "a²i", to: "ai̯²" },
      { from: "a³i", to: "ai̯³" },
      { from: "a⁴i", to: "ai̯⁴" },
      { from: "ai", to: "ai̯" },
      { from: "e¹i", to: "ei̯¹" },
      { from: "e²i", to: "ei̯²" },
      { from: "e³i", to: "ei̯³" },
      { from: "e⁴i", to: "ei̯⁴" },
      { from: "ei", to: "ei̯" },
      { from: "a¹o", to: "au̯¹" },
      { from: "a²o", to: "au̯²" },
      { from: "a³o", to: "au̯³" },
      { from: "a⁴o", to: "au̯⁴" },
      { from: "ao", to: "au̯" },
      { from: "o¹u", to: "ou̯¹" },
      { from: "o²u", to: "ou̯²" },
      { from: "o³u", to: "ou̯³" },
      { from: "o⁴u", to: "ou̯⁴" },
      { from: "ou", to: "ou̯" },
      { from: "a", to: "a", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "e", to: "ə", next: { or: ["/n/", "/¹n/", "/²n/", "/³n/", "/⁴n/"] } },
      { from: "e¹r", to: "aɝ̯¹" },
      { from: "e²r", to: "aɝ̯²" },
      { from: "e³r", to: "aɝ̯³" },
      { from: "e⁴r", to: "aɝ̯⁴" },
      { from: "er", to: "aɝ̯" },
      { comment: "", isBlankLine: true },
      { comment: "plain vowels" },
      { from: "i", to: "j", next: ["vowel"] },
      { from: "i", to: "ɨ", next: ["#word"] },
      { from: "e", to: "ɤ" },
      { from: "a", to: "a" },
      { from: "u", to: "y", previous: { or: ["/j/", "/q/", "/y/", "/x/"] } },
      { from: "u", to: "u" },
      { from: "o", to: "ɔ" },
      { from: "ü", to: "y" },
      { comment: "", isBlankLine: true },
      { comment: "consonants" },
      { from: "zh", to: "ʈ͡ʂ" },
      { from: "ch", to: "ʈ͡ʂʰ" },
      { from: "sh", to: "ʂ" },
      { from: "b", to: "p˭" },
      { from: "p", to: "pʰ" },
      { from: "m", to: "m" },
      { from: "f", to: "f" },
      { from: "d", to: "t˭" },
      { from: "t", to: "tʰ" },
      { from: "n", to: "n" },
      { from: "l", to: "l" },
      { from: "g", to: "k˭" },
      { from: "k", to: "k", next: "w" },
      { from: "k", to: "kʰ" },
      { from: "h", to: "x" },
      { from: "j", to: "t͡ɕ" },
      { from: "q", to: "t͡ɕʰ" },
      { from: "x", to: "ɕ" },
      { from: "r", to: "ɻ" },
      { from: "z", to: "t͡s" },
      { from: "c", to: "t͡sʰ" },
      { from: "s", to: "s" },
      { from: "w", to: "w" },
      { from: "y", to: "j" },
      { comment: "", isBlankLine: true },
      { comment: "", isBlankLine: true },
      { comment: "" },
      { comment: "final pass" }
    ],
    [
      { comment: "", isBlankLine: true },
      { comment: "convert tones" },
      { from: "¹", to: "˥" },
      { from: "²", to: "˧˥" },
      { from: "³", to: "˨˩˦" },
      { from: "⁴", to: "˥˩" },
      { comment: "", isBlankLine: true },
      { comment: "tweak diphthongs" },
      { from: "ai̯", to: "aɪ̯" },
      { from: "au̯", to: "ãõ̯" },
      { from: "ei̯", to: "ɛi̯" },
      { from: "ou̯", to: "ɑ̝ʊ̯" }
    ]
  ],
  phonemeLengths: {
    map: { "aɪ̯": 300, "ãõ̯": 300, "ɛi̯": 300, "ɑ̝ʊ̯": 300, "aɝ̯": 300 }
  }
}

const spanishRules: RuleSet = {
  preprocess: ["lowerCase", "cleanPunctuation"],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.PreferIpaPlus,
  rules: [
    { comment: 'turn an acute accent into a primary stress marker' },
    { from: "á", to: "ˈa" }, { from: "é", to: "ˈe" }, { from: "í", to: "ˈi" }, { from: "ó", to: "ˈo" }, { from: "ú", to: "ˈu" },
    { comment: 'mark the second vowel in diphthongs' },
    { from: "i", to: "i̯", previous: { or: ["/a/", "/e/", "/o/"] } },
    { from: "o", to: "o̯", previous: "a" },
    { from: "u", to: "u̯", previous: { or: ["/a/", "/e/", "/o/"] } },
    { comment: '', isBlankLine: true },
    { comment: 'convert consonants and various combinations' },
    { from: "b", to: "b", previous: ["#word"] },
    { from: "b", to: "b", previous: { or: ["/m/", "/n/"] } },
    { from: "b", to: "β" },
    { from: "ch", to: "t͡ʃ" },
    { from: "cci", to: "kθj", next: ["vowel"] },
    { from: "cci", to: "kθi", next: ["consonant"] },
    { from: "c", to: "θ", next: { or: ["/e/", "/i/"] } },
    { from: "c", to: "k" },
    { from: "d", to: "ð", previous: { or: ["/a/", "/e/", "/i/", "/o/", "/u/", "/r/", "/ɾ/"] }, next: { or: [":vowel", "#word"] } },
    { from: "d", to: "d" },
    { from: "f", to: "f" },
    { from: "gü", to: "gw" },
    { from: "gu", to: "gu", next: ["consonant"] },
    { from: "gu", to: "ɣ", previous: { or: ["/a/", "/e/", "/i/", "/o/", "/u/", "/r/", "/ɾ/"] }, next: { or: ["/e/", "/i/"] } },
    { from: "gu", to: "ɣw", previous: { or: ["/a/", "/e/", "/i/", "/o/", "/u/", "/r/", "/ɾ/"] }, next: ["vowel"] },
    { from: "gu", to: "g" },
    { from: "g", to: "x", next: { or: ["/e/", "/i/"] } },
    { from: "g", to: "ɣ", previous: { or: ["/a/", "/e/", "/i/", "/o/", "/u/", "/r/", "/ɾ/"] }, next: { or: [":vowel", "#word"] } },
    { from: "g", to: "g" },
    { from: "j", to: "x" },
    { from: "ll", to: "ʎ" },
    { from: "l", to: "l" },
    { from: "m", to: "m" },
    { from: "ñ", to: "ɲ" },
    { from: "ñ", to: "ɲ" },
    { from: "n", to: "n", next: { seq: ["/g/", { or: ["/e/", "/i/"] }] } },
    { from: "n", to: "ŋ", next: "g" },
    { from: "n", to: "n" },
    { from: "p", to: "β", next: "t" },
    { from: "p", to: "p" },
    { from: "qü", to: "kw" },
    { from: "qu", to: "k" },
    { from: "rr", to: "r" },
    { from: "r", to: "r", previous: ["#word"] },
    { from: "r", to: "ɾ" },
    { from: "s", to: "z", next: ["voiced"] },
    { from: "s", to: "s" },
    { from: "tz", to: "ts" },
    { from: "t", to: "t" },
    { from: "v", to: "b", previous: ["#word"] },
    { from: "v", to: "b", previous: { or: ["/m/", "/n/"] } },
    { from: "v", to: "β" },
    { from: "y", to: "i", previous: ["#word"], next: ["#word"] },
    { from: "y", to: "j", next: ["#word"] },
    { from: "y", to: "ʝ" },
    { from: "x", to: "s", previous: ["#word"] },
    { from: "x", to: "ks" },
    { from: "z", to: "ð̬", next: ["voiced"] },
    { from: "z", to: "θ" },
    { comment: '', isBlankLine: true },
    { from: "i", to: "j", previous: ["consonant"], next: ["vowel"] },
    { from: "u", to: "w", previous: ["consonant"], next: ["vowel"] },
    { from: "h", to: "" }
  ],
  phonemeLengths: {
    map: { "a": 160, "e": 160, "i": 160, "o": 160, "u": 160 }
  },
  stressRule: {
    default: -1,
    toCheck: [
      { which: 0, pattern: { seq: ["#word", { or: ["/de/", "/es/", "/el/", "/la/", "/se/", "/te/"] }, "#word"] } },
      { which: -1, pattern: { seq: [{ and: [":vowel", ":non-syllabic"] }, "#word"] } },
      { which: -2, pattern: { seq: [{ or: [":vowel", "/n/", "/s/"] }, "#word"] } }
    ]
  }
};

export const sampleRules: Record<string, RuleSet> = {
  'Ancient Greek': ancientGreekRules,
  Esperanto: esperantoRules,
  Futhorc: futhorcRules,
  Hiragana: hiraganaRules,
  Pinyin: pinyinRules,
  Spanish: spanishRules,
};
